<template>
  <div class="px-2" style="max-width: 100%; overflow-x: hidden;">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9E6D2B"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#9E6D2B" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- TOP HEADER & CONTROLS -->
    <v-row align="center" justify="space-between" class="mb-3 mx-0">
      <v-col cols="12" sm="6" md="6" class="py-1">
        <div class="d-flex align-center">
          <span class="mainHeader">Menu Management</span>
          <span class="role-count-pill ml-3">{{ rolesList.length }} Roles</span>
        </div>
      </v-col>
      <v-col cols="12" sm="6" md="6" class="d-flex align-center justify-end py-1">
        <v-btn
          color="#9E6D2B"
          dark
          height="40"
          elevation="1"
          class="text-none font-weight-bold px-4"
          style="border-radius: 8px;"
          @click="refreshAll"
          :disabled="appLoading"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh Roles
        </v-btn>
      </v-col>
    </v-row>

    <!-- PRO KPI METRIC CARDS -->
    <v-row class="mb-4 mx-0">
      <v-col cols="12" sm="4" md="4" class="py-1 pa-2">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">CONFIGURED STAFF ROLES</span>
            <v-icon small color="#9E6D2B">mdi-shield-account-outline</v-icon>
          </div>
          <div class="kpi-val">{{ rolesList.length }} <small class="text-caption">Active Roles</small></div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4" md="4" class="py-1 pa-2">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">AVAILABLE SYSTEM MENUS</span>
            <v-icon small color="#047857">mdi-view-grid-plus-outline</v-icon>
          </div>
          <div class="kpi-val text-green">{{ menuData ? menuData.length : '—' }} <small class="text-caption">Modules</small></div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4" md="4" class="py-1 pa-2">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">ACCESS SECURITY LEVEL</span>
            <v-icon small color="#2563EB">mdi-lock-check-outline</v-icon>
          </div>
          <div class="kpi-val text-blue text-subtitle-1 font-weight-bold">Role-Based Access Control</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- FILTER & SEARCH BAR -->
    <div class="filter-search-wrap mb-4">
      <div class="search-input-wrap" style="width: 100%; max-width: 320px;">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          placeholder="Filter role name..."
          outlined
          dense
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </div>
    </div>

    <!-- NO ROLES STATE -->
    <div v-if="!appLoading && computedFilteredRoles.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#9E6D2B" large class="mb-2">mdi-account-search-outline</v-icon>
      <div class="no-data-text">No staff roles found. Add staff roles in Settings or Staff Management.</div>
    </div>

    <!-- ROLES GRID -->
    <v-row v-if="computedFilteredRoles.length > 0" class="mb-5 mx-0">
      <v-col
        v-for="(item, i) in computedFilteredRoles"
        :key="item._id || i"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        class="py-2"
      >
        <v-card outlined class="role-card rounded-xl pa-4 elevation-1 d-flex flex-column justify-space-between" color="#FFFFFF">
          <div>
            <div class="d-flex align-center justify-space-between mb-3">
              <v-avatar color="#F5EBDC" size="40">
                <v-icon color="#7D551E" size="20">mdi-shield-account-outline</v-icon>
              </v-avatar>
              <span class="status-pill-active">Role Active</span>
            </div>

            <div class="role-label-title mb-1">STAFF ROLE</div>
            <h3 class="role-name-text mb-3">{{ item.roles || 'Unnamed Role' }}</h3>
          </div>

          <div class="mt-2 pt-3 border-top-divider">
            <v-dialog persistent v-model="item.menu" max-width="660">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  color="#9E6D2B"
                  outlined
                  block
                  height="38"
                  class="text-none font-weight-bold rounded-lg"
                  @click="getPosition(item._id)"
                >
                  <v-icon left small>mdi-cog-outline</v-icon>
                  Configure Permissions
                </v-btn>
              </template>

              <v-card class="rounded-xl overflow-hidden pa-0" color="#FFFFFF">
                <!-- Dialog Header -->
                <div class="dialog-header px-5 py-4 d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-avatar color="#F5EBDC" size="36" class="mr-3">
                      <v-icon color="#7D551E" size="20">mdi-shield-lock-outline</v-icon>
                    </v-avatar>
                    <div>
                      <h3 class="dialog-title">Menu Access Control</h3>
                      <div class="dialog-subtitle">Configuring permissions for <strong>{{ item.roles }}</strong></div>
                    </div>
                  </div>
                  <button class="close-btn" @click="item.menu = false">
                    <v-icon size="18" color="#64748b">mdi-close</v-icon>
                  </button>
                </div>

                <v-divider></v-divider>

                <!-- Dialog Body -->
                <div class="pa-5 modal-body-container" style="max-height: 480px; overflow-y: auto;">
                  <div class="mb-3 d-flex align-center justify-space-between">
                    <span class="text-caption font-weight-bold text-uppercase text-muted">SELECT ACCESSIBLE MODULES</span>
                    <span class="text-caption gold-text-sm">Check modules to grant permission</span>
                  </div>

                  <div v-if="menuData && menuData.length > 0">
                    <div
                      v-for="(mItem, j) in menuData"
                      :key="j"
                      class="menu-module-block mb-3 pa-3 rounded-lg"
                    >
                      <div class="d-flex align-center justify-space-between">
                        <v-checkbox
                          v-model="mItem.isChecked"
                          :label="mItem.name"
                          color="#9E6D2B"
                          hide-details
                          class="mt-0 pt-0 font-weight-bold main-checkbox"
                          @click="reCheck(mItem, mItem.isChecked)"
                        ></v-checkbox>
                        <v-chip x-small color="#F5EBDC" class="text-caption font-weight-bold" style="color: #7D551E;" v-if="mItem.submenus && mItem.submenus.length">
                          {{ mItem.submenus.length }} Submodules
                        </v-chip>
                      </div>

                      <!-- Submenus List -->
                      <div v-if="mItem.isChecked && mItem.submenus && mItem.submenus.length > 0" class="submenus-grid mt-2 pt-2 border-top-sub">
                        <div
                          v-for="(sub, k) in mItem.submenus"
                          :key="k"
                          class="submenu-item-wrap"
                        >
                          <v-checkbox
                            v-model="sub.isChecked"
                            :label="sub.name"
                            color="#9E6D2B"
                            hide-details
                            class="mt-0 pt-0 sub-checkbox"
                          ></v-checkbox>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="text-center py-6">
                    <v-icon color="#9E6D2B" large class="mb-2">mdi-alert-circle-outline</v-icon>
                    <div class="text-body-2 text-muted">No menu list data available for this role.</div>
                  </div>
                </div>

                <v-divider></v-divider>

                <!-- Dialog Footer Actions -->
                <div class="px-5 py-3 dialog-footer d-flex align-center justify-end gap-2">
                  <v-btn
                    text
                    height="36"
                    class="text-none font-weight-bold"
                    color="#64748b"
                    @click="item.menu = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="#9E6D2B"
                    dark
                    height="38"
                    elevation="1"
                    class="text-none font-weight-bold px-5 rounded-lg"
                    @click="updateMenu(i)"
                    :disabled="appLoading"
                  >
                    <v-icon left small>mdi-content-save-outline</v-icon>
                    Save Permissions
                  </v-btn>
                </div>
              </v-card>
            </v-dialog>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "MenuManagementPage",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      menuData: [],
      rolesList: [],
      searchQuery: "",
      positionId: null,
    };
  },
  computed: {
    computedFilteredRoles() {
      let list = this.rolesList;
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter(r => (r.roles || "").toLowerCase().includes(q));
      }
      return list;
    },
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    refreshAll() {
      this.getRoless();
      this.getMenu();
    },
    getMenu() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/admin/menu/list",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.menuData = response.data.data || [];
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to fetch menus.";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    getRoless() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/getalllist/roles",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.rolesList = response.data.data || [];
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to fetch roles.";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    reCheck(item, check) {
      if (!check && item.submenus) {
        for (let i = 0; i < item.submenus.length; i++) {
          item.submenus[i].isChecked = false;
        }
      }
    },
    updateMenu(i) {
      this.appLoading = true;
      var data = {};
      data["roleId"] = this.positionId;
      data["menus"] = this.menuData;
      axios({
        url: "/rolemenu/add/edit",
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status) {
            this.msg = response.data.msg || "Role permissions updated successfully";
            this.showsnackbar = true;
            this.menuData = null;
            if (this.computedFilteredRoles[i]) {
              this.computedFilteredRoles[i].menu = false;
            }
            this.getMenu();
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to update permissions.";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    getPosition(id) {
      this.positionId = id;
      this.menuData = null;
      this.appLoading = true;
      axios({
        url: "/get/roles",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.menuData = response.data.menus || [];
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.mainHeader {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  font-weight: 700;
  color: #1E1815;
  letter-spacing: 0.3px;
}

.role-count-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  background: #F5EBDC;
  color: #7D551E;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

/* KPI CARDS */
.kpi-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #8C7D6F;
  text-transform: uppercase;
}

.kpi-val {
  font-size: 18px;
  font-weight: 800;
  color: #1E1815;
}

.text-blue { color: #2563EB; }
.text-green { color: #047857; }

/* FILTER & SEARCH */
.filter-search-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: #FFFFFF;
  padding: 10px 14px;
  border: 1px solid #EFE8DD;
  border-radius: 12px;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #8C7D6F;
  font-weight: 500;
}

/* ROLE CARDS */
.role-card {
  border: 1px solid #EFE8DD;
  transition: all 0.2s ease;
  min-height: 170px;
}

.role-card:hover {
  border-color: #9E6D2B;
  box-shadow: 0 4px 16px rgba(158, 109, 43, 0.08) !important;
  transform: translateY(-2px);
}

.status-pill-active {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  background: #ECFDF5;
  color: #047857;
  font-size: 11px;
  font-weight: 700;
}

.role-label-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #8C7D6F;
  text-transform: uppercase;
}

.role-name-text {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #1E1815;
  margin: 0;
  line-height: 1.3;
}

.border-top-divider {
  border-top: 1px solid #F3EFE8;
}

/* DIALOG STYLING */
.dialog-header {
  background: #FAF8F5;
}

.dialog-title {
  font-family: 'Cinzel', serif;
  font-size: 17px;
  font-weight: 700;
  color: #1E1815;
  margin: 0;
}

.dialog-subtitle {
  font-size: 12.5px;
  color: #64748b;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
}

.close-btn:hover {
  background: #EFE8DD;
}

.gold-text-sm {
  color: #9E6D2B;
  font-weight: 600;
}

.menu-module-block {
  background: #FAF8F5;
  border: 1px solid #EFE8DD;
}

.border-top-sub {
  border-top: 1px dashed #EFE8DD;
}

.submenus-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  padding-left: 28px;
}

.sub-checkbox >>> .v-label {
  font-size: 13px !important;
  color: #475569 !important;
}

.main-checkbox >>> .v-label {
  font-size: 14.5px !important;
  font-weight: 700 !important;
  color: #1E1815 !important;
}

.dialog-footer {
  background: #FAF8F5;
}
</style>
